import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import {
  TransitionIncludedStatusApi,
  TransitionIncludedStatus,
  NewTransitionIncludedStatus,
  TransitionIncludedStatusFilters,
} from '../models';

const transitionIncludedStatusesService = createApi({
  reducerPath: 'transitionIncludedStatusesService',
  baseQuery: createBaseQueryWithReauth(
    `${API_BUSINESS_URL}/transition-included-statuses`
  ),
  tagTypes: ['TransitionIncludedStatuses', 'Transitions'],
  endpoints: (builder) => ({
    getTransitionIncludedStatuses: builder.query<
      TransitionIncludedStatus[],
      { filters?: TransitionIncludedStatusFilters }
    >({
      query: ({ filters }) => {
        const params: Record<string, string> = {};
        if (filters?.transitionId && filters.transitionId.trim() !== '') {
          params.transitionId = filters.transitionId.trim();
        }
        return {
          url: '',
          params: Object.keys(params).length > 0 ? params : undefined,
        };
      },
      transformResponse: (response: TransitionIncludedStatusApi[]) =>
        response.map((item) => ({
          id: item.id,
          transitionId: item.transitionId,
          includedStatusId: item.includedStatusId,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        })),
      providesTags: ['TransitionIncludedStatuses'],
    }),
    getTransitionIncludedStatusById: builder.query<
      TransitionIncludedStatus,
      { includedStatusId: string }
    >({
      query: ({ includedStatusId }) => ({
        url: `/${includedStatusId}`,
      }),
      transformResponse: (response: TransitionIncludedStatusApi) => ({
        id: response.id,
        transitionId: response.transitionId,
        includedStatusId: response.includedStatusId,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      providesTags: (_result, _error, { includedStatusId }) => [
        { type: 'TransitionIncludedStatuses' as const, id: includedStatusId },
      ],
    }),
    createTransitionIncludedStatus: builder.mutation<
      TransitionIncludedStatus,
      NewTransitionIncludedStatus
    >({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      transformResponse: (response: TransitionIncludedStatusApi) => ({
        id: response.id,
        transitionId: response.transitionId,
        includedStatusId: response.includedStatusId,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      invalidatesTags: ['TransitionIncludedStatuses', 'Transitions'],
    }),
    deleteTransitionIncludedStatus: builder.mutation<void, string>({
      query: (includedStatusId) => ({
        url: `/${includedStatusId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TransitionIncludedStatuses', 'Transitions'],
    }),
  }),
});

export default transitionIncludedStatusesService;
export const {
  useGetTransitionIncludedStatusesQuery,
  useGetTransitionIncludedStatusByIdQuery,
  useCreateTransitionIncludedStatusMutation,
  useDeleteTransitionIncludedStatusMutation,
} = transitionIncludedStatusesService;
